import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner, Image, Modal } from "react-bootstrap";
import { Snackbar } from "@material-ui/core";
import Select from "react-select";

import useDemoForm, { providerSupportsPresets } from "./hooks/useDemoForm";
import CompressionDropdown from "./components/CompressionDropdown";
import AudioFormatDropdown from "./components/AudioFormatDropdown";
import VoiceDropdown from "./components/VoiceDropdown";
import TextAreaWithCounter from "./components/TextAreaWithCounter";
import AudioPlayer from "./components/AudioPlayer";
import { PageWrapper } from "./styles";

export const FormDemo = () => {
	const {
		formik,
		models,
		loading,
		audioUrl,
		presets,
		loadingPresets,
		fetchVcSpeakers
	} = useDemoForm();

	const resolvePresetIdentifier = preset => preset?.id ?? preset?.name ?? preset?.presetId;
	const selectedPreset = presets.find(preset => resolvePresetIdentifier(preset) === formik.values.preset);
	const presetOptions = useMemo(() => {
		return presets.map(preset => {
			const value = resolvePresetIdentifier(preset) ?? null;
			const volume = preset.volume ?? "—";
			const silenceEdgeMs = preset.silenceEdgeMs ?? "—";
			return {
				value,
				label: `${preset.name ?? value} (Volume: ${volume}, Silence: ${silenceEdgeMs}ms)`,
				preset,
			};
		});
	}, [presets]);

	const selectedProvider = models.find(m => (m.name ?? m.model ?? "") === formik.values.model)?.provider ?? null;

	const [showErrors, setShowErrors] = useState(false);

	useEffect(() => {
		if (Object.keys(formik.errors).length > 0) {
			// console.log("Erros de validação:", formik.errors);
		}
	}, [formik.errors]);

	return (
		<PageWrapper>
			<>
				<Modal show={loading} centered backdrop="static" keyboard={false}>
					<Modal.Body style={{ textAlign: "center", padding: "40px" }}>
						<Spinner animation="border" role="status" className="mb-3" />
						<p style={{ marginTop: "16px", color: "#666" }}>Carregando Sintetizador...</p>
					</Modal.Body>
				</Modal>

				<Snackbar
					open={formik.errors.general}
					autoHideDuration={3000}
					onClose={() => formik.setFieldError("general", null)}
					message={formik.errors.general}
				/>
				<form
					onSubmit={async (e) => {
						e.preventDefault();
						setShowErrors(true);
						const errors = await formik.validateForm();
						if (Object.keys(errors).length === 0) {
							formik.handleSubmit();
						}
					}}
					noValidate
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						padding: "20px",
						borderRadius: "10px",
						margin: "20px auto",
						maxWidth: "800px",
						boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
						backgroundColor: "#1e1e1e"
					}}
				>
					{showErrors && Object.keys(formik.errors).length > 0 && (
						<div style={{
							backgroundColor: "#f44336",
							color: "#fff",
							padding: "12px 16px",
							borderRadius: "5px",
							fontSize: "14px"
						}}>
							{Object.values(formik.errors).map((error, index) => (
								<div key={index}>• {error}</div>
							))}
						</div>
					)}

					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: "10px",
							justifyContent: "flex-start",
							alignItems: "flex-start"
						}}
					>
						<CompressionDropdown
							value={formik.values.compression}
							onChange={value => {
								formik.setFieldValue("compression", value);
							}}
							disabled={loading}
						/>
						<AudioFormatDropdown
							value={formik.values.audioFormat}
							onChange={value => {
								formik.setFieldValue("audioFormat", value);
							}}
							disabled={loading}
							options={[
								{ value: "WAV", label: "WAV" },
								{ value: "MP3", label: "MP3" },
								{ value: "OGG", label: "OGG" }
							]}
						/>
						<VoiceDropdown
							value={formik.values.model}
							options={models.map(model => ({ value: model.name, label: model.name }))}
							onChange={value => {
								formik.setFieldValue("model", value);
								formik.setFieldValue("preset", null);
								const provider = models.find(m => (m.name ?? m.model ?? '') === value)?.provider ?? null;
								fetchVcSpeakers(provider);
							}}
							loading={loading}
							disabled={loading}
						/>
					</div>
					{providerSupportsPresets(selectedProvider) && (
						<>
							<label
								style={{
									color: "#fff",
									fontSize: "11px",
									marginBottom: "0px",
									marginTop: "3px"
								}}
							>
								Configuração (Preset)
							</label>
							<Select
								value={presetOptions.find(option => option.value === formik.values.preset) || null}
								onChange={option => {
									const presetValue = option?.value ?? null;
									formik.setFieldValue("preset", presetValue);
								}}
								isDisabled={loading || loadingPresets}
								isLoading={loadingPresets}
								options={presetOptions}
								isSearchable
								isClearable
								placeholder="Padrão"
								noOptionsMessage={() =>
									loadingPresets
										? "Carregando presets..."
										: presets.length === 0
											? "Sem presets disponíveis"
											: "Nenhum preset corresponde ao filtro"
								}
								styles={{
									control: base => ({
										...base,
										backgroundColor: "#343a40",
										borderColor: "#343a40",
										color: "#fff",
										minWidth: "220px"
									}),
									singleValue: base => ({
										...base,
										color: "#fff"
									}),
									input: base => ({
										...base,
										color: "#fff"
									}),
									menu: base => ({
										...base,
										backgroundColor: "#1e1e1e"
									}),
									option: (base, state) => ({
										...base,
										backgroundColor: state.isFocused ? "#4f4d4f" : "#1e1e1e",
										color: state.isFocused ? "#fff" : "#ccc"
									})
								}}
							/>
						</>
					)}

					<div style={{ display: "flex", flexDirection: "column" }}>
						<TextAreaWithCounter
							value={formik.values.text}
							onChange={e => {
								formik.setFieldValue("text", e.target.value);
							}}
							rows={5}
						/>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: "10px"
						}}
					>
						<div style={{ position: "relative", display: "inline-block" }}>
							<Button
								type="submit"
								disabled={loading || !formik.isValid || formik.isSubmitting}
								style={{
									border: (!formik.isValid && !loading) ? "2px solid #f44336" : "none",
									cursor: (!formik.isValid && !loading) ? "not-allowed" : "pointer",
									transition: "all 0.2s ease"
								}}
								title={!formik.isValid && !loading ? "Preencha todos os campos obrigatórios" : ""}
							>
								{loading ? (
									<Spinner animation="border" role="status" />
								) : (
									<>
										<strong className="text-light"> Sintetizar </strong>
										<Image
											roundedCircle
											src="/images/icons/icone-audio.png"
											height={40}
											width={23}
										/>
									</>
								)}
							</Button>
							{!formik.isValid && !loading && (
								<span
									style={{
										position: "absolute",
										right: "-30px",
										top: "50%",
										transform: "translateY(-50%)",
										fontSize: "20px",
										cursor: "not-allowed"
									}}
									title="Formulário inválido"
								>
								</span>
							)}
						</div>

						<AudioPlayer
							key={audioUrl}
							src={audioUrl || ""}
							format={formik.values.audioFormat?.toLowerCase() || ""}
							onDownload={() => {
								if (audioUrl) {
									const filename = `blackbolt-audio-${new Date().toISOString()}.${formik.values.audioFormat.toLowerCase()}`;
									const link = document.createElement("a");
									link.href = audioUrl;
									link.setAttribute("download", filename);
									document.body.appendChild(link);
									link.click();
									document.body.removeChild(link);
								}
							}}
							display={audioUrl ? 'flex' : 'none'}
						/>

						{audioUrl && (
							<Button
								onClick={() => {
									if (audioUrl) {
										const filename = `blackbolt-audio-${new Date().toISOString()}.${formik.values.audioFormat.toLowerCase()}`;
										const link = document.createElement("a");
										link.href = audioUrl;
										link.setAttribute("download", filename);
										document.body.appendChild(link);
										link.click();
										document.body.removeChild(link);
									}
								}}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "8px",
									padding: "8px 16px"
								}}
							>
								<strong className="text-light">Download</strong>
								<span role="img" aria-label="download" style={{ fontSize: "20px" }}>⬇️</span>
							</Button>
						)}
					</div>
				</form>
			</>
		</PageWrapper>
	);
};
